import type { Collection, TierCollectionCard } from "@/interfaces/collection";
import type { Product } from "@/interfaces/product";
import { apiClient } from "@/lib/apiClient";
import type { BudgetCollectionsResponse } from "@/types/types";

export async function fetchCollections(): Promise<Collection[]> {
  const { data } = await apiClient.get<{ collections: Collection[] }>("/collections");
  return data.collections;
}

// One card per budget tier (base collection image + display name) for the Collections browse page.
export async function fetchBaseTierCollections(): Promise<TierCollectionCard[]> {
  const { data } = await apiClient.get<{ tiers: TierCollectionCard[] }>("/collections/base-tiers");
  return data.tiers;
}

// The tier's composed product feed — collections-scoped twin of campaignBuilder's by-budget call,
// so the Collections tier-detail page never depends on CAMPAIGN_BUILDER permissions.
export async function fetchCollectionsByBudget(budget: number): Promise<BudgetCollectionsResponse> {
  const { data } = await apiClient.get<{ status: string } & BudgetCollectionsResponse>(
    "/collections/by-budget",
    { params: { budget } },
  );
  return { collections: data.collections, domesticIndex: data.domesticIndex };
}

// NOTE (recipient flow): the response is cast directly to Product[] with no field
// mapping. The recipient collection reuses this via useCollectionProducts and expects
// the repo Product shape (imageUrl / impactTag / impactImage). When wiring the real
// recipient endpoint, confirm it returns Product-shaped fields — otherwise
// imageUrl/impactTag/impactImage come back undefined and cards/impact render blank.
export async function fetchCollectionProducts(collectionId: string): Promise<Product[]> {
  const { data } = await apiClient.get<{ products: Product[] }>(
    `/collections/${collectionId}/products`,
  );
  return data.products;
}

export async function fetchProductDetails(productId: string): Promise<Product> {
  const { data } = await apiClient.get<{ product: Product }>(
    `/collections/products-details/${productId}`,
  );
  return data.product;
}
